import { KeyboardEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChevronLeft, ChevronRight } from '@digital-twins/design-system/icons'

import { FeaturedQuote, QuoteItem } from '@/components/quick-study-results/QuickStudyOpenEndCard'
import { cn } from '@/lib/utils'

/** Up to this many quotes the slider shows dots; past it, a "3 / 9" counter stays clean. */
export const STUDY_DOC_QUOTE_DOTS_MAX = 7

interface StudyDocQuotesSliderProps {
  /** Already ordered: featured first, then the rest. */
  items: Array<QuoteItem>
}

const NAV_BUTTON_CLASS =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

function wrapIndex(next: number, count: number): number {
  return ((next % count) + count) % count
}

/**
 * One quote at a time at featured prominence. Every slide stays mounted and
 * stacked in one grid cell, so the band is as tall as its longest quote and
 * the page never jumps between slides. Navigation wraps around at both ends.
 */
export function StudyDocQuotesSlider(props: StudyDocQuotesSliderProps) {
  const { items } = props

  const { t } = useTranslation('app')
  const [index, setIndex] = useState(0)

  const count = items.length
  const active = count > 0 ? Math.min(index, count - 1) : 0
  const hasControls = count > 1
  const hasDots = count <= STUDY_DOC_QUOTE_DOTS_MAX

  const goTo = (next: number) => {
    setIndex(wrapIndex(next, count))
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!hasControls) {
      return
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      goTo(active - 1)
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      goTo(active + 1)
    }
  }

  if (count === 0) {
    return null
  }

  const position = t('studyDocChrome.quotePosition', { current: active + 1, total: count })

  const indicator = hasDots ? (
    <div className="flex items-center justify-center gap-1" data-testid="study-doc-quote-dots">
      {items.map((item, dotIndex) => {
        const isActive = dotIndex === active

        return (
          <button
            key={item.key}
            type="button"
            onClick={() => goTo(dotIndex)}
            aria-label={t('studyDocChrome.quotePosition', { current: dotIndex + 1, total: count })}
            aria-current={isActive ? 'true' : undefined}
            className="group/dot flex items-center justify-center p-1.5 focus-visible:outline-none"
            data-testid={`study-doc-quote-dot-${dotIndex}`}
            data-active={isActive ? 'true' : 'false'}
          >
            <span
              className={cn(
                'h-1.5 rounded-full transition-all motion-reduce:transition-none group-focus-visible/dot:ring-2 group-focus-visible/dot:ring-ring',
                isActive ? 'w-4 bg-foreground' : 'w-1.5 bg-foreground/25 group-hover/dot:bg-foreground/45',
              )}
            />
          </button>
        )
      })}
    </div>
  ) : (
    <span
      className="min-w-[3.5rem] text-center text-sm tabular-nums text-muted-foreground"
      data-testid="study-doc-quote-counter"
    >
      {active + 1} / {count}
    </span>
  )

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label={t('studyDocChrome.inTheirWords')}
      tabIndex={hasControls ? 0 : undefined}
      onKeyDown={onKeyDown}
      className="space-y-3 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid="study-doc-quote-slider"
      data-active-index={active}
    >
      <div className="grid" data-testid="study-doc-quote-slider-track">
        {items.map((item, slideIndex) => {
          const isActive = slideIndex === active

          return (
            <div
              key={item.key}
              role="group"
              aria-roledescription="slide"
              aria-label={t('studyDocChrome.quotePosition', { current: slideIndex + 1, total: count })}
              aria-hidden={isActive ? undefined : true}
              className={cn(
                '[grid-area:1/1] [&>figure]:h-full',
                'transition-[opacity,transform,visibility] duration-300 ease-out',
                'motion-reduce:translate-x-0 motion-reduce:transition-none',
                isActive && 'visible translate-x-0 opacity-100',
                !isActive && 'pointer-events-none invisible opacity-0',
                !isActive && (slideIndex < active ? '-translate-x-3' : 'translate-x-3'),
              )}
              data-testid="study-doc-quote-embed"
              data-quote-id={item.key}
              data-active={isActive ? 'true' : 'false'}
            >
              <FeaturedQuote item={item} />
            </div>
          )
        })}
      </div>
      {hasControls ? (
        <div className="flex items-center justify-center gap-3" data-testid="study-doc-quote-slider-controls">
          <button
            type="button"
            onClick={() => goTo(active - 1)}
            aria-label={t('studyDocChrome.previousQuote')}
            className={NAV_BUTTON_CLASS}
            data-testid="study-doc-quote-prev"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {indicator}
          <button
            type="button"
            onClick={() => goTo(active + 1)}
            aria-label={t('studyDocChrome.nextQuote')}
            className={NAV_BUTTON_CLASS}
            data-testid="study-doc-quote-next"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <span className="sr-only" aria-live="polite" data-testid="study-doc-quote-slider-status">
            {position}
          </span>
        </div>
      ) : null}
    </div>
  )
}
